import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { KeyRound, Waves } from "lucide-react";
import { supabase } from "../supabaseClient";

function getDefaultPath() {
    return Capacitor.isNativePlatform() ? "/home" : "/";
}

export default function UpdatePassword() {
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    const [status, setStatus] = useState("Checking your recovery session…");

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            const { data, error } = await supabase.auth.getSession();
            if (!mounted) return;

            if (error) {
                setStatus(error.message || "Could not start password recovery.");
                return;
            }

            if (data?.session) {
                setReady(true);
                setStatus("");
                return;
            }

            setStatus("This password reset link is invalid or expired. Request a fresh one from login.");
        };

        init();

        const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
            if (!mounted) return;

            if (event === "PASSWORD_RECOVERY" || session) {
                setReady(true);
                setStatus("");
            }
        });

        return () => {
            mounted = false;
            sub.subscription.unsubscribe();
        };
    }, []);

    const passwordsMatch = password === confirm && confirm.length > 0;
    const passwordReady = password.length >= 6;

    const canSubmit = useMemo(() => {
        return ready && passwordReady && passwordsMatch && !busy;
    }, [ready, passwordReady, passwordsMatch, busy]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("");

        if (password !== confirm) {
            setStatus("Passwords do not match.");
            return;
        }

        setBusy(true);
        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) {
                setStatus(error.message);
                return;
            }

            setStatus("Password updated. Taking you back to your account…");
            window.setTimeout(() => navigate("/account", { replace: true }), 700);
        } catch (err) {
            console.error(err);
            setStatus("Could not update password.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className={`auth-shell auth-shell--coastal ${isNative ? "auth-shell--native" : "auth-shell--web"}`}>
            <section className="auth-coast-card auth-coast-card--compact">
                <header className="auth-logo-header" aria-label="Beach Bum Pass">
                    <Link className="auth-logo-link" to={getDefaultPath()} aria-label="Beach Bum Pass home">
                        <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-logo-mark" />
                        <span className="auth-logo-text">Beach Bum Pass</span>
                    </Link>
                </header>

                <aside className="auth-brand-panel auth-brand-panel--simple" aria-label="Beach Bum Pass password reset brand panel">
                    <div className="auth-desktop-logo-lockup" aria-hidden="true">
                        <div className="auth-desktop-logo-orb">
                            <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-brand-logo" />
                        </div>
                        <div className="auth-desktop-brand-copy">
                            <span>Beach Bum Pass</span>
                            <small>Account rescue</small>
                        </div>
                    </div>

                    <div className="auth-brand-copy">
                        <p className="auth-eyebrow">Password reset</p>
                        <h1 className="auth-title auth-title--hero">Fresh key, same beach pass.</h1>
                        <p className="auth-subtitle auth-subtitle--hero">
                            Choose a new password from your recovery link, then get right back to managing your trip.
                        </p>
                    </div>

                    <div className="auth-brand-wave" aria-hidden="true">
                        <Waves size={18} />
                        <span>Coast mode</span>
                    </div>
                </aside>

                <section className="auth-form-panel" aria-labelledby="update-password-title">
                    <div className="auth-form-heading">
                        <p className="auth-eyebrow">New password</p>
                        <h2 id="update-password-title" className="auth-title">Set your new password.</h2>
                        <p className="auth-subtitle">Use the reset link from your email, then choose a password for future logins.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <label className="auth-label">
                            New password
                            <span className="auth-input-wrap">
                                <KeyRound size={18} aria-hidden="true" />
                                <input
                                    className="auth-input"
                                    type="password"
                                    placeholder="Min 6 characters"
                                    value={password}
                                    autoComplete="new-password"
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={!ready || busy}
                                    required
                                />
                            </span>
                        </label>

                        <label className="auth-label">
                            Confirm new password
                            <input
                                className="auth-input"
                                type="password"
                                placeholder="Re-enter password"
                                value={confirm}
                                autoComplete="new-password"
                                onChange={(e) => setConfirm(e.target.value)}
                                disabled={!ready || busy}
                                required
                            />
                        </label>

                        <div className="auth-checklist" aria-label="Password checklist">
                            <span className={passwordReady ? "is-ready" : ""}>6+ characters</span>
                            <span className={passwordsMatch ? "is-ready" : ""}>Passwords match</span>
                        </div>

                        <button className="auth-primary" type="submit" disabled={!canSubmit}>
                            {busy ? "Saving…" : "Update password"}
                        </button>

                        {status ? <div className="auth-msg" aria-live="polite">{status}</div> : null}

                        <div className="auth-row auth-row--center">
                            <Link className="auth-link" to="/login">Back to login</Link>
                        </div>
                    </form>
                </section>
            </section>
        </main>
    );
}
